import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useAuth from "hooks/useAuth";
import { Button, StatusBadge, LoadingState, ErrorState } from "components/ui";

const IconCheck = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
       strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const PlanDetail = () => {
  const axiosPrivate = useAxiosPrivate();
  const { planId } = useParams();
  const navigate = useNavigate();
  const { auth } = useAuth();
  const isLoggedIn = !!(auth?.accessToken || localStorage.getItem("accessToken"));

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const res = await axiosPrivate.get(`/api/plans/${planId}`);
        setPlan(res.data);
      } catch (_) {
        setError("Plan not found.");
      } finally {
        setLoading(false);
      }
    };
    fetchPlan();
  }, [planId]);

  const handleSubscribe = () => {
    if (!isLoggedIn) navigate("/auth/login");
    else navigate("/tenant/subscribe", { state: { planId: plan.id } });
  };

  const shellStyle = {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #0A0A1A 0%, #1E1B4B 55%, #312E81 100%)",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
    position: "relative",
    overflow: "hidden",
  };

  const decoration = (
    <>
      <div aria-hidden="true" style={{ position: "absolute", width: 520, height: 520, borderRadius: 999, filter: "blur(120px)", opacity: 0.4, background: "rgba(79,70,229,0.55)", top: -160, left: -120, pointerEvents: "none" }} />
      <div aria-hidden="true" style={{ position: "absolute", width: 420, height: 420, borderRadius: 999, filter: "blur(120px)", opacity: 0.35, background: "rgba(127,119,221,0.65)", bottom: -120, right: -80, pointerEvents: "none" }} />
    </>
  );

  if (loading) {
    return (
      <div style={shellStyle}>{decoration}
        <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 20, padding: 32, border: "1px solid rgba(255,255,255,0.08)" }}>
          <LoadingState label="Loading plan…" />
        </div>
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div style={shellStyle}>{decoration}
        <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 20, padding: 32, border: "1px solid rgba(255,255,255,0.08)", maxWidth: 480, width: "100%" }}>
          <ErrorState
            title="Plan not found"
            description={error || "We couldn't load this plan."}
            action={<Button variant="inverse" onClick={() => navigate(-1)}>Go back</Button>}
          />
        </div>
      </div>
    );
  }

  const isEnterprise = plan.type === "ENTERPRISE";
  const features = [
    `${plan.maxBranches} Branch${plan.maxBranches > 1 ? "es" : ""}`,
    `${plan.maxUsersPerBranch} Users per branch`,
    `${plan.maxProductsPerBranch} Products per branch`,
    ...(plan.type === "PRO" ? ["SMS / WhatsApp notifications", "Advanced reports"] : []),
    ...(plan.type === "ENTERPRISE" ? ["AI integrations", "Blockchain traceability", "24/7 dedicated support"] : []),
  ];

  return (
    <div style={shellStyle}>
      {decoration}
      <div
        style={{
          maxWidth: 720, width: "100%",
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.10)",
          borderRadius: 24,
          padding: "48px 40px",
          backdropFilter: "blur(20px)",
          boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <span
            style={{
              display: "inline-block",
              padding: "5px 14px",
              background: "rgba(127,119,221,0.18)",
              border: "1px solid rgba(127,119,221,0.35)",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              color: "#C7D2FE",
              textTransform: "uppercase",
              letterSpacing: "0.12em",
              marginBottom: 16,
            }}
          >
            {plan.type}
          </span>
          <h1
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 700,
              fontSize: "clamp(28px, 6vw, 48px)",
              margin: 0,
              letterSpacing: "-0.02em",
              color: "#fff",
            }}
          >
            {plan.name}
          </h1>
          {plan.description && (
            <p style={{ fontFamily: "var(--font-body)", fontSize: 15, color: "rgba(255,255,255,0.65)", marginTop: 12, lineHeight: 1.6 }}>
              {plan.description}
            </p>
          )}
        </div>

        {/* Pricing */}
        <div
          style={{
            textAlign: "center",
            marginBottom: 28,
            padding: 24,
            background: "rgba(79,70,229,0.12)",
            borderRadius: 16,
            border: "1px solid rgba(127,119,221,0.25)",
          }}
        >
          <div style={{ fontFamily: "var(--font-body)", fontSize: 13, color: "rgba(255,255,255,0.55)", marginBottom: 8, letterSpacing: "0.04em", textTransform: "uppercase" }}>
            {isEnterprise ? "Custom pricing" : "Starts at"}
          </div>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 800,
              fontSize: 56,
              color: "#fff",
              lineHeight: 1,
              letterSpacing: "-0.03em",
            }}
          >
            {isEnterprise ? "Custom" : `${plan.monthlyPrice} ${plan.currency}`}
          </div>
          {!isEnterprise && (
            <div style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "rgba(255,255,255,0.5)", marginTop: 8 }}>
              / month · {plan.yearlyPrice} {plan.currency} / year
            </div>
          )}
        </div>

        {/* Features */}
        <div style={{ marginBottom: 28 }}>
          <h3
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 600,
              fontSize: 16,
              marginBottom: 14,
              color: "rgba(255,255,255,0.92)",
            }}
          >
            What's included
          </h3>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "grid", gap: 10 }}>
            {features.map((f, i) => (
              <li key={i} style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 14, color: "rgba(255,255,255,0.78)" }}>
                <span
                  style={{
                    width: 22, height: 22, borderRadius: 999,
                    background: "rgba(79,70,229,0.25)",
                    border: "1px solid rgba(127,119,221,0.5)",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    color: "#C7D2FE", flexShrink: 0,
                  }}
                >
                  <IconCheck />
                </span>
                {f}
              </li>
            ))}
          </ul>
        </div>

        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <StatusBadge value={plan.active ? "ACTIVE" : "INACTIVE"} dot />
        </div>

        <Button block variant="primary" onClick={handleSubscribe}>
          {isEnterprise ? "Contact our team" : "Subscribe now"}
        </Button>

        <p style={{ textAlign: "center", marginTop: 14, fontSize: 12, color: "rgba(255,255,255,0.45)" }}>
          No commitment · Cancel anytime
        </p>
      </div>
    </div>
  );
};

export default PlanDetail;
